import { FC, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import styled from "@emotion/styled";
import { api } from "@/modules/api";
import { atlasRoutes } from "@/app/router/routes";
import { DataImportItemSummarySchema } from "@/domains/openapi/types";
import { Uuid } from "@/domains/global/identifiers";
import { Button } from "@radix-ui/themes";
import { DateTime } from "luxon";
import { DataImportItemsTable } from "@/pages/data-import-items/shared/data-import-items-table";

export const DataImportDataImportItemsPage: FC = () => {
  const navigate = useNavigate();
  const { dataImportId } = useParams<{ dataImportId: Uuid }>();

  if (!dataImportId) {
    return <div>Invalid ID</div>;
  }

  const pageSize = 1000;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["data-import-items", dataImportId],
    queryFn: () =>
      api.post("/internal/v1/data-import-items/search", {
        body: {
          filter_by_data_import_id: dataImportId,
          limit: pageSize,
        },
      }),
    staleTime: DateTime.fromObject({ minute: 1 }).toMillis(),
  });

  const handleViewDataImportItem = (id: string) => {
    navigate(atlasRoutes.dataImportItemsView({ params: { dataImportItemId: id } }).path);
  };

  const handleViewDataImport = () => {
    navigate(atlasRoutes.dataImportsView({ params: { dataImportId } }).path);
  };

  const tableData = useMemo<DataImportItemSummarySchema[]>(() => data?.data?.results ?? [], [data]);

  return (
    <Container>
      <h1>Data Import Items</h1>

      <Button variant="outline" onClick={() => handleViewDataImport()}>
        View Data Import
      </Button>
      <br />
      <br />

      {/* Loading and Error States */}
      {isLoading && <div>Loading...</div>}
      {isError && <div>Error fetching data imports: {error.message}</div>}

      {/* Table or No Results */}
      {tableData.length > 0 ? (
        <DataImportItemsTable
          data={tableData}
          onViewDataImportItem={handleViewDataImportItem}
          pageSize={pageSize}
        />
      ) : (
        !isLoading && <div>No data import items found.</div>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;
